import type { AnchorHTMLAttributes, ReactNode } from 'react';
import { forwardRef } from 'react';
import styles from './Link.module.css';

type TrackingValue = string | number | boolean;

export interface LinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  width?: 'full' | 'auto';
  children: ReactNode;
  external?: boolean;
  trackEvent?: string;
  trackData?: Record<string, TrackingValue | null | undefined>;
}

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      width = 'auto',
      external = false,
      className,
      children,
      href,
      trackEvent,
      trackData,
      ...props
    },
    ref
  ) => {
    const classNames = [
      styles.link,
      styles[variant],
      styles[size],
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const linkProps = {
      ...props,
      ref,
      href,
      className: classNames,
      ...(external && {
        target: '_blank',
        rel: 'noopener noreferrer',
      }),
    };

    const trackingAttributes: Record<string, string> = {};
    if (trackEvent) {
      trackingAttributes['data-track'] = trackEvent;

      if (trackData) {
        for (const [key, value] of Object.entries(trackData)) {
          if (value == null) continue;
          const attrKey = `data-${key.replace(/[A-Z]/g, m => `-${m.toLowerCase()}`)}`;
          trackingAttributes[attrKey] = String(value);
        }
      }
    }

    return (
      <a {...trackingAttributes} {...linkProps}>
        {children}
      </a>
    );
  }
);

Link.displayName = 'Link';



